import { Button, RadioGroup, RadioGroupProps, Snackbar } from "@mcleod/components";
import { AbstractComponentDemoExamples } from "./AbstractComponentDemoExamples";
import { LiveExampleComponents } from "../LiveExampleComponents";
import { ValueDisplayValue } from "@mcleod/core";

export abstract class DemoRadioGroupExamples extends AbstractComponentDemoExamples {
    protected override registerExamples() {
        super.registerExamples();
        this.registerPropertyExamples("afterSelection", {
            examples: [
                { value: true, description: "This example demonstrates when the after selection event fires.  The user will be shown a snackbar after the selection is completed." }
            ],
            onCreate: (exampleComponents: LiveExampleComponents) => {
                const radioGroup = (exampleComponents.liveComponent) as RadioGroup;
                radioGroup.addAfterSelectionListener((event) => {
                    Snackbar.showSnackbar(`New selection: ${event.newSelection.displayValue}`);
                });
            }
        });
        this.registerPropertyExamples("beforeSelection", {
            examples: [
                { value: true, description: "This example demonstrates how a before selection event can be used to cancel the selection.  Specifically, the user will not be allowed to select the 'Second Choice' option when the previously selection option was 'First Choice'." }
            ],
            onCreate: (exampleComponents: LiveExampleComponents) => {
                const radioGroup = (exampleComponents.liveComponent) as RadioGroup;
                radioGroup.addBeforeSelectionListener((event) => {
                    if (event.oldSelection?.value === "1" && event.newSelection.value === "2") {
                        event.preventDefault();
                        Snackbar.showWarningSnackbar("Selection disallowed; a selection of Second Choice is not valid when the previous selection was First Choice.");
                    }
                });
            }
        });
        this.registerPropertyExamples("caption", {
            examples: [
                { value: "Options" },
                { value: "A Somewhat Longer Caption" },
            ]
        });
        this.registerPropertyExamples("focusable", {
            examples: [
                { value: true, description: "Note how you can tab into this Radio and that it maintains focus after toggling the value (via the spacebar)." },
                { value: false, description: "This Radio isn't allowed to gain focus." },
            ]
        });
        this.registerPropertyExamples("printable", {
            examples: [
                { value: true, description: "This example shows how a Radio Group appears when in printable mode." }
            ],
            onCreate: (exampleComponents: LiveExampleComponents) => {
                const button = new Button({
                    caption: "Toggle Printable",
                    backgroundColor: "primary",
                    color: "primary.reverse",
                    marginLeft: 15,
                    onClick: () => {
                        const group = exampleComponents.liveComponent as RadioGroup;
                        group.printable = !group.printable;
                    }
                });
                exampleComponents.liveComponent.rowBreak = false;
                exampleComponents.livePanel.add(button);
            }
        });
        this.registerPropertyExamples("required", {
            examples: [
                { value: true, description: "To see how a radio group appears when it is required, click to the left of the radio group (in the empty area), and tab through all of the radio buttons without selecting one."}
            ],
            onCreate: (exampleComponents: LiveExampleComponents) => {
                const button = new Button({
                    caption: "Validate",
                    width: 150,
                    backgroundColor: "primary",
                    color: "primary.reverse",
                    marginLeft: 15,
                    onClick: () => {
                        const group = exampleComponents.liveComponent as RadioGroup;
                        if (button.caption === "Validate") {  
                            group.validate();
                            button.caption = "Reset Validation";
                        }
                        else {
                            group.resetValidation();
                            button.caption = "Validate";
                        }
                    }
                });
                exampleComponents.liveComponent.rowBreak = false;
                exampleComponents.livePanel.add(button);
            }
        });
    }

    protected selectFirstOption(exampleComponents: LiveExampleComponents) {
        const group = exampleComponents.liveComponent as RadioGroup;
        this.selectOption(group, 0);
    }

    protected selectOption(group: RadioGroup, index: number) {
        group.selectedOption = group.options[index];
    }

    protected override get propsForAllExamples(): Partial<RadioGroupProps> {
        return {
            ...super.propsForAllExamples,
            id: "demoRadioGroup",
            caption: "Options",
            items: [
                new ValueDisplayValue("1", "First Choice"),
                new ValueDisplayValue("2", "Second Choice"),
                new ValueDisplayValue("3", "Third Choice")
            ]
        };
    }
}
